import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ShieldCheck, Loader2, ArrowLeft, FileText, ScrollText, Heart } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import LazLogo from "@/components/LazLogo";

const ICONS = {
  terms: ScrollText,
  privacy: ShieldCheck,
  ethics: Heart,
};

export default function LegalCenter() {
  const { document } = useParams();
  const docName = (document || "privacy").toLowerCase();
  const { user } = useAuth();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [consents, setConsents] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [d, c] = await Promise.all([
        api.get(`/legal/${docName}`),
        user ? api.get("/legal/me").catch(() => ({ data: null })) : Promise.resolve({ data: null }),
      ]);
      setDoc(d.data);
      setConsents(c.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docName, user?.id]);

  const accept = async () => {
    if (!doc) return;
    setAccepting(true);
    try {
      await api.post("/legal/accept", { document: doc.name, version: doc.version });
      toast.success(`${doc.title} accepted.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setAccepting(false);
    }
  };

  const accepted =
    consents && doc && consents[doc.name]?.version === doc.version;

  if (loading || !doc) {
    return (
      <div className="min-h-screen flex items-center justify-center text-stone-500">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  const Icon = ICONS[doc.name] || FileText;

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="border-b border-stone-200 bg-stone-50/85 backdrop-blur-xl sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <LazLogo size={36} />
          <Link
            to={user ? "/dashboard" : "/"}
            className="text-sm text-stone-500 hover:text-stone-900 inline-flex items-center gap-1"
            data-testid="legal-back-link"
          >
            <ArrowLeft size={14} strokeWidth={1.5} /> Back
          </Link>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-12">
        {/* Nav between docs */}
        <nav className="flex flex-wrap gap-2 mb-8" data-testid="legal-nav">
          {["terms", "privacy", "ethics"].map((d) => {
            const I = ICONS[d];
            const isActive = d === docName;
            return (
              <Link
                key={d}
                to={`/legal/${d}`}
                data-testid={`legal-tab-${d}`}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs transition-all ${
                  isActive
                    ? "bg-amber-700 text-stone-50"
                    : "bg-stone-100 text-stone-700 hover:bg-stone-200"
                }`}
              >
                <I size={13} strokeWidth={1.5} />
                {d === "terms"
                  ? "Coaching Terms"
                  : d === "privacy"
                  ? "Privacy & Data"
                  : "Code of Ethics"}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-start gap-4 mb-2">
          <div className="h-12 w-12 rounded-2xl bg-amber-700/10 text-amber-800 flex items-center justify-center shrink-0">
            <Icon size={20} strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="font-serif text-3xl sm:text-4xl text-stone-900 tracking-tight leading-tight">
              {doc.title}
            </h1>
            <div className="text-xs text-stone-500 mt-1 uppercase tracking-[0.18em]">
              Version {doc.version}
            </div>
          </div>
        </div>

        {user && (
          <div
            data-testid="legal-status"
            className={`mt-6 rounded-2xl border p-4 flex items-center justify-between gap-4 ${
              accepted
                ? "bg-green-700/5 border-green-700/30"
                : "bg-amber-700/5 border-amber-700/30"
            }`}
          >
            <div className="text-sm">
              {accepted ? (
                <>
                  <span className="text-green-800 font-medium">Accepted</span>
                  <span className="text-stone-500">
                    {" "}
                    on{" "}
                    {new Date(consents[doc.name].accepted_at).toLocaleString()}
                  </span>
                </>
              ) : (
                <span className="text-stone-700">
                  You have not accepted this version yet.
                </span>
              )}
            </div>
            {!accepted && (
              <Button
                data-testid="legal-accept-button"
                onClick={accept}
                disabled={accepting}
                className="rounded-full bg-amber-700 hover:bg-amber-800 text-stone-50"
              >
                {accepting && <Loader2 className="animate-spin mr-2" size={14} />}
                I accept
              </Button>
            )}
          </div>
        )}

        <article
          data-testid="legal-body"
          className="mt-8 rounded-3xl bg-stone-50 border border-stone-200 p-6 sm:p-10 space-y-4 text-stone-700 leading-relaxed"
        >
          {(() => {
            const lines = doc.body.split("\n");
            const out = [];
            let para = [];
            let list = [];

            // Safe inline bold renderer — splits on **bold** and emits React nodes.
            // No HTML injection: user input could never reach here, but we still
            // avoid dangerouslySetInnerHTML entirely.
            const renderInline = (text) => {
              const parts = text.split(/(\*\*[^*]+\*\*)/g);
              return parts.map((part, idx) => {
                if (part.startsWith("**") && part.endsWith("**")) {
                  return (
                    <strong key={idx} className="text-stone-900">
                      {part.slice(2, -2)}
                    </strong>
                  );
                }
                return <span key={idx}>{part}</span>;
              });
            };

            const flushPara = () => {
              if (para.length) {
                out.push(
                  <p
                    key={`p-${out.length}`}
                    className="text-stone-700 leading-relaxed"
                  >
                    {renderInline(para.join(" "))}
                  </p>
                );
                para = [];
              }
            };
            const flushList = () => {
              if (list.length) {
                out.push(
                  <ul key={`u-${out.length}`} className="list-disc pl-6 space-y-2 text-stone-700">
                    {list.map((item, k) => (
                      <li key={`li-${out.length}-${k}`}>{renderInline(item)}</li>
                    ))}
                  </ul>
                );
                list = [];
              }
            };

            for (const raw of lines) {
              const line = raw.trim();
              if (!line) {
                flushPara();
                flushList();
                continue;
              }
              if (line.startsWith("# ")) {
                flushPara();
                flushList();
                continue;
              }
              if (line.startsWith("## ")) {
                flushPara();
                flushList();
                out.push(
                  <h2
                    key={`h-${out.length}`}
                    className="font-serif text-2xl text-stone-900 mt-8 pt-2 border-t border-stone-200"
                  >
                    {line.replace(/^##\s+/, "")}
                  </h2>
                );
                continue;
              }
              if (line.startsWith("- ")) {
                flushPara();
                list.push(line.slice(2));
                continue;
              }
              if (line.startsWith("**") && line.endsWith("**")) {
                flushPara();
                flushList();
                out.push(
                  <p key={`q-${out.length}`} className="italic text-stone-500 text-sm">
                    {line.replace(/\*\*/g, "")}
                  </p>
                );
                continue;
              }
              para.push(line);
            }
            flushPara();
            flushList();
            return out;
          })()}
        </article>
      </div>
    </div>
  );
}
